import React, { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "components/common/LoadingSpinner";

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");

  const { signIn, resetPassword, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the path user was trying to visit, default to /admin/programs
  const from = (location.state as any)?.from?.pathname || "/admin/programs";

  // If already authenticated, redirect to intended destination
  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const result = await signIn(email, password, rememberMe);

      if (result.success) {
        // Successful login - will redirect via the Navigate component above
        navigate(from, { replace: true });
      } else {
        setError(result.error || "Failed to sign in");
      }
    } catch (err) {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setResetMessage("");

    if (!resetEmail) {
      setError("Please enter your email address");
      return;
    }

    try {
      const result = await resetPassword(resetEmail);

      if (result.success) {
        setResetMessage("Password reset email sent! Check your inbox.");
        setShowResetForm(false);
        setResetEmail("");
      } else {
        setError(result.error || "Failed to send reset email");
      }
    } catch (err) {
      setError("An unexpected error occurred");
    }
  };

  // Show loading spinner while checking initial auth state
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white dark:bg-navy-900">
        <LoadingSpinner bare />
      </div>
    );
  }

  if (showResetForm) {
    return (
      <div className="w-full">
        <div className="w-full flex-col items-center">
          <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
            Reset Password
          </h4>
          <p className="mb-9 ml-1 text-base text-gray-600">
            Enter your email to receive a password reset link
          </p>

          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {resetMessage && (
            <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3">
              <p className="text-sm text-green-800">{resetMessage}</p>
            </div>
          )}

          <form onSubmit={handleResetPassword}>
            <div className="mb-6">
              <label
                htmlFor="reset-email"
                className="ml-1.5 text-sm font-medium text-navy-700 dark:text-white"
              >
                Email*
              </label>
              <input
                type="email"
                id="reset-email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="admin@biafit.com"
                className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none dark:border-white/10 dark:text-white"
                required
              />
            </div>

            <button
              type="submit"
              className="linear mt-2 w-full rounded-xl bg-biafit-primary py-[12px] text-base font-medium text-white transition duration-200 hover:bg-biafit-secondary active:bg-biafit-secondary dark:bg-biafit-secondary dark:text-white dark:hover:bg-biafit-primary dark:active:bg-biafit-primary"
            >
              Send Reset Link
            </button>
          </form>

          <button
            onClick={() => {
              setShowResetForm(false);
              setError("");
              setResetMessage("");
            }}
            className="mt-4 text-sm font-medium text-biafit-primary hover:text-biafit-secondary dark:text-white"
          >
            ← Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Sign in section */}
      <div className="w-full flex-col items-center">
        <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
          Sign In
        </h4>
        <p className="mb-9 ml-1 text-base text-gray-600">
          Administrator access only
        </p>

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {resetMessage && (
          <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3">
            <p className="text-sm text-green-800">{resetMessage}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Email */}
          <div className="mb-3">
            <label
              htmlFor="email"
              className="ml-1.5 text-sm font-medium text-navy-700 dark:text-white"
            >
              Email*
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@biafit.com"
              className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none dark:border-white/10 dark:text-white"
              required
            />
          </div>

          {/* Password */}
          <div className="mb-3">
            <label
              htmlFor="password"
              className="ml-1.5 text-sm font-medium text-navy-700 dark:text-white"
            >
              Password*
            </label>
            <div className="relative mt-2">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 pr-12 text-sm outline-none dark:border-white/10 dark:text-white"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {showPassword ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-5 w-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-5 w-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Checkbox and Forgot Password */}
          <div className="mb-4 flex items-center justify-between px-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="remember-me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-biafit-primary focus:ring-biafit-primary"
              />
              <label
                htmlFor="remember-me"
                className="ml-2 text-sm font-medium text-navy-700 dark:text-white"
              >
                Remember me
              </label>
            </div>
            {/* <button
              type="button"
              onClick={() => {
                setShowResetForm(true)
                setError('')
              }}
              className="text-sm font-medium text-biafit-primary hover:text-biafit-secondary dark:text-white"
            >
              Forgot Password?
            </button> */}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="linear mt-2 w-full rounded-xl bg-biafit-primary py-[12px] text-base font-medium text-white transition duration-200 hover:bg-biafit-secondary active:bg-biafit-secondary disabled:cursor-not-allowed disabled:opacity-50 dark:bg-biafit-secondary dark:text-white dark:hover:bg-biafit-primary dark:active:bg-biafit-primary"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <LoadingSpinner bare size="sm" tone="white" className="mr-2" />
                Signing In...
              </div>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        <div className="mt-4 text-center">
          <span className="text-sm font-medium text-navy-700 dark:text-gray-600">
            Need admin access? Contact your system administrator.
          </span>
        </div>
      </div>
    </div>
  );
}
